import { playsTheField, valueOf, type MatchupSummary } from '@fsp/domain';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { cameFrom, useScreenName } from '../shell/wayBack';
import { morph } from '../shell/morph';
import type { LineupProblem, LineupRowView, LineupView, StatRange } from '@fsp/domain';
import {
  useHome,
  useLeagueBase,
  useReaderZone,
  useLineup,
  useMyTeamId,
  useScoreboard,
  useStandings,
} from '../data/hooks';
import { useViewedPeriod } from '../data/period';
import { useLeagueNow } from '../data/demo';
import { useSim } from '../data/SimProvider';
import { useLeagueSource } from '../data/leagueSource';
import { saveLineup } from '../data/lineup';
import { messageOf } from '../data/api';
import { useAtLeast } from '../data/detail';
import { useTools } from '../data/tools';
import { useDelight } from '../delight/useDelight';
import {
  Cap,
  GameLabel,
  LiveDot,
  PositionPill,
  ProTeamCode,
  SlotButton,
  StatusBadge,
  TeamMark,
} from '../kit';
import { Icon } from '../kit/Icon';
import { TeamBanner, hasBanner } from '../kit/TeamBanner';
import { TeamFigure, teamFace } from '../kit/TeamFigure';
import { useFinePointer } from '../kit/viewport';
import { Button, Chip, Choice, IconButton, Line, OneOf, Text, tell } from '../kit/ui';
import { useChatDrawer } from '../shell/chatDrawer';
import { MyStats } from './myTeam/MyStats';
import { STAT_RANGES } from './shared/StatsTable';
import '../styles/screens/league-screens.css';
import { WorkbenchScreen } from '../templates/WorkbenchScreen';
import { useTier } from '../shell/useTier';
import { HealthBar } from './shared/HealthBar';
import { AttentionStrip, attentionItems } from './shared/AttentionStrip';
import { LineupSheet, type DragProps } from './shared/LineupSheet';
import { Proj, ProjectionWhy, projectionNote } from './shared/Projection';
import { PointsWhy } from './shared/PointsWhy';
import { MatchupChip } from './shared/MatchupChip';
import { StatsNote } from './shared/StatsNote';
import { FixLineupSheet } from './shared/FixLineupSheet';
import { useActiveLeague } from '../data/LeaguesProvider';
import { lockTime, lockWords } from './shared/lock';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import { RosterMoveSheet, type RosterMove } from './shared/RosterMoveSheet';
import { num } from './shared/notComputed';
import { FromLeague } from './shared/FromLeague';
import { useEditTeam } from '../shell/editTeam';
import { useMyTeamHere } from '../data/team';
import { useCardFor, PlayerCardsOpenIn } from '../shell/playerCard';
import { useRemembered } from '../shell/tabMemory';
import { leagueIdFromPath } from '../data/route';
import { Term } from '../kit/Why';
import { TeamNews } from './myTeam/TeamNews';
import { TeamDoors } from './myTeam/TeamDoors';
import { TeamSide, TOOLS, type SideTool } from './myTeam/TeamSide';
import { RosterDock } from './myTeam/RosterDock';
import { SlotPanel, type ChosenSlot } from './myTeam/SlotPanel';
import { DetailSheet } from '../templates/DetailSheet';
import '../styles/screens/my-team.css';

// A team's lineup (LINEUP.md). Drawn through the kit (LOOKS.md Part 7).
//
// MY TEAM IS THE LEAGUE'S LANDING (B-211), AND IT IS FOR MANAGING THE TEAM
// (DIRECTION U13, B-315): a league opens here, on the roster, because setting
// a lineup is what a manager does all week. Its top is what needs the team —
// a slot that cannot score, what waits on me, my players' news — and from a
// tablet up the screens a roster decision needs sit beside the lineup in one
// panel (U21, B-316). The head to head with this week's opponent is Game
// Day's (U22).

/**
 * Split for E12, like Home, Schedule and Scoreboard.
 *
 * TWO ROUTES, ONE SCREEN. The league's own address is my team (B-211; `/lineup`
 * redirects there); `/teams/:teamId` is any team
 * in the league — so a manager can see who has which players (PLAN item D).
 * Any member may
 * read any team's lineup, and `view.team.isMine` decides whether the screen
 * lets you change it. Another manager's team is the same rows with nothing to
 * tap but a player's card.
 */
/**
 * My Team, or another team's page. `room` is a tool taking the room beside the
 * roster's dock (B-400): its address is the tool's own (`/players`,
 * `/waivers`…), from a tablet up, on my own team.
 */
export function LineupScreen({ room }: { room?: SideTool } = {}) {
  const { teamId: routeTeamId } = useParams();
  const myTeamId = useMyTeamId();
  const teamId = routeTeamId ?? myTeamId;
  // WHAT THE SERVER ANSWERED A SAVE WITH, shown in place of the read it
  // replaced: the endpoint returns the whole lineup as stored, which is newer
  // than the read and is exactly what the next read would say.
  const [saved, setSaved] = useState<LineupView | null>(null);
  // THE WEEK ON SCREEN (D4, B-216): a real league reads any week it plays; the
  // simulator keeps only the current week's lineup, so a practice league's
  // stays on now and the stepper does not offer another there.
  const source = useLeagueSource();
  const { period } = useViewedPeriod();
  const week = source === 'api' ? period : undefined;
  // THE WEEK'S SCOREBOARD STARTS WITH THE LINEUP'S READ, not after it: the
  // body asks for it once the lineup has answered (another team's game, B-220),
  // and joins this in flight or takes its answer (`readStore.ts`).
  useScoreboard(week);
  return (
    <FromLeague snapshot={useLineup(teamId, week)}>
      {(view) => {
        // A save answered for this team AND this week: switching weeks after
        // a save must not draw the saved week under the new week's label.
        const shown =
          saved !== null &&
          saved.teamId === view.teamId &&
          saved.period.index === view.period.index &&
          saved.updatedAt >= view.updatedAt
            ? saved
            : view;
        return (
          <LineupBody
            view={shown}
            teamId={teamId ?? view.teamId}
            week={week}
            onSaved={setSaved}
            room={room}
          />
        );
      }}
    </FromLeague>
  );
}

/**
 * Whose team this is: the badge, the name, the owner and the record.
 *
 * ON EVERY TEAM, MINE INCLUDED, since SCENARIOS L2. It showed only on somebody else's
 * team, in place of the sandbox, which left my own page with nothing on it
 * that said whose it was — and nowhere to change the name or the colors. Mine
 * carries **Edit team** beside the name. One slim row; Plan ahead, which sat
 * beside Edit team, is a door on a phone and a panel wider (B-316).
 *
 * ITS BANNER BEHIND IT (PLAN-2026-10-24-team-banners §4), mine and anybody
 * else's, and from a tablet up the team's character standing at its end; a
 * phone keeps the slim row with the badge.
 */
function TeamHeader({
  view,
  onEdit,
  onFix,
  theirs,
  visiting,
}: {
  view: LineupView;
  onEdit?: () => void;
  /** A commissioner's fix, for a week that is over (J1). */
  onFix?: () => void;
  /** Another team's matchup the week on screen, for the line under its name (B-220); two in a week of two games (Q6). */
  theirs?: readonly MatchupSummary[];
  /** Another team's page (B-378): its neighbors in the table, and what I can do with it. */
  visiting?: Visiting | null;
}) {
  const t = view.team;
  const navigate = useNavigate();
  const played = t.record.wins + t.record.losses + t.record.ties > 0;
  // WRITE TO ITS MANAGER (I8), beside their name: a person's team, not mine.
  const chat = useChatDrawer();
  const writeTo = t.isMine ? null : chat.whoToMessage(t.id);
  const canMessage = writeTo !== null;
  const message = async () => {
    const done = await chat.message(t.id);
    if (!done.ok) tell(done.message, 'bad');
  };
  const banner = hasBanner(t);
  const figure = teamFace(t) !== null;
  return (
    <div
      className={`fsp-team-header${banner ? ' fsp-has-banner' : ''}${figure ? ' has-figure' : ''}`}
      data-mine={t.isMine || undefined}
    >
      {banner ? <TeamBanner team={t} /> : null}
      <TeamMark team={t} size={t.isMine ? 32 : 36} />
      <div className="fsp-team-header-body">
        <div className="fsp-name">
          {/* Where a team's name pressed elsewhere grows to (B-404). */}
          <span className="fsp-name-text fsp-hero-target">{t.name}</span>
          {onEdit ? (
            <span className="fsp-team-edit">
              <Button variant="quiet" size="xs" icon="edit" onClick={onEdit}>
                Edit team
              </Button>
            </span>
          ) : null}
          {onFix ? (
            <span className="fsp-team-edit">
              <Button variant="quiet" size="xs" icon="veto" onClick={onFix}>
                Fix lineup
              </Button>
            </span>
          ) : null}
        </div>
        <div className="fsp-sub">
          {t.ownerName}
          {played
            ? ` · ${t.record.wins}-${t.record.losses}${t.record.ties ? `-${t.record.ties}` : ''}`
            : ''}
        </div>
        {(theirs ?? []).map((m) => (
          <TheirMatchup key={m.id} m={m} teamId={t.id} />
        ))}
        {canMessage ||
        (visiting &&
          (visiting.trade !== null || visiting.ahead !== null || visiting.season !== null)) ? (
          <Line gap={2} wrap>
            {canMessage ? (
              <Button
                size="xs"
                variant="quiet"
                icon="chat"
                label={`Message ${writeTo}`}
                onClick={() => void message()}
              >
                Message
              </Button>
            ) : null}
            {visiting && visiting.trade !== null ? (
              <Button size="xs" icon="trade" to={visiting.trade}>
                Propose a trade
              </Button>
            ) : null}
            {visiting && visiting.ahead !== null ? (
              <Button size="xs" variant="quiet" icon="when" to={visiting.ahead}>
                Weeks ahead
              </Button>
            ) : null}
            {visiting && visiting.season !== null ? (
              <Button size="xs" variant="quiet" icon="history" to={visiting.season}>
                Season
              </Button>
            ) : null}
          </Line>
        ) : null}
      </div>
      <span className="fsp-team-header-period">
        {/* THE NEXT TEAM ALONG, in the table's order (B-378): looking
            through the league's rosters was back to Standings each time. */}
        {visiting && visiting.prev !== null && visiting.next !== null ? (
          <span className="fsp-team-switch">
            <IconButton
              icon="previous"
              variant="quiet"
              size="sm"
              label={`Previous team, ${visiting.prev.name}`}
              onClick={() => navigate(visiting.prev!.to)}
            />
            <IconButton
              icon="next"
              variant="quiet"
              size="sm"
              label={`Next team, ${visiting.next.name}`}
              onClick={() => navigate(visiting.next!.to)}
            />
          </span>
        ) : null}
        <Cap>{view.period.label}</Cap>
      </span>
      {figure ? <TeamFigure team={t} height={88} className="fsp-team-header-figure" /> : null}
    </div>
  );
}

/** What another team's page offers beside its roster (B-378). */
interface Visiting {
  /** The trade builder with this team filled in, or null when I cannot trade. */
  trade: string | null;
  /** This team's weeks ahead, on a phone, which has no side panel for them. */
  ahead: string | null;
  /** This team's season on the schedule, every result and every week to come (B-386). */
  season: string | null;
  prev: { name: string; to: string } | null;
  next: { name: string; to: string } | null;
}

/**
 * ANOTHER TEAM'S GAME THAT WEEK, one tap from its roster (B-220): "vs Rico's
 * Team · 71.0–88.2", their score first, opening that matchup. My own week's
 * game is Game Day's (DIRECTION U22); a team I am looking at had nothing that
 * said who it was playing.
 */
function TheirMatchup({ m, teamId }: { m: MatchupSummary; teamId: string }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const us = m.home.team.id === teamId ? m.home : m.away;
  const them = m.home.team.id === teamId ? m.away : m.home;
  const score = m.status === 'pre' ? '' : ` · ${us.score.toFixed(1)}–${them.score.toFixed(1)}`;
  return (
    <div className="fsp-team-header-game">
      <button
        type="button"
        className="fsp-linklike fsp-name-link"
        onClick={() => navigate(`${base}/matchup/${m.id}`)}
      >
        <span className="fsp-team-header-game-text">
          {m.status === 'final' ? 'Final · ' : ''}vs {them.team.name}
          {score}
        </span>
        <Icon name="next" size="sm" />
      </button>
    </div>
  );
}

/** A lineup cell: a slot and which of its kind. */
type Cell = { slot: string; slotIndex: number };

/** This device has made a move, or said it knows how (B-288). */
const MOVE_TIP_KEY = 'fsp.lineup.moveTipSeen';

const sameCell = (a: Cell, b: Cell) => a.slot === b.slot && a.slotIndex === b.slotIndex;

/** Whether the player in `from` (or nobody) may sit in `into`'s cell. */
function fits(from: LineupRowView, into: LineupRowView): boolean {
  if (from.player === null) return true;
  if (into.slotKind === 'starter') return from.eligibleSlots.includes(into.slot);
  if (into.slotKind === 'reserve') return from.reserveEligible;
  // THE TAXI SQUAD (PLAN-2026-10-27-the-next-season §2.7): a player within
  // its seasons, before the first kickoff. Coming off it is any time.
  if (into.slotKind === 'taxi') return from.taxiEligible;
  return true;
}

/**
 * Whether two rows can trade places (B-275): two different rows with at least
 * one player between them, neither locked, each player landing where he may
 * play, and not two bench rows or two IR rows, whose trade changes nothing.
 * The server holds the rest (roster limits, IR room) and says so in words.
 */
function canTrade(a: LineupRowView, b: LineupRowView, ignoreLock = false): boolean {
  if (sameCell(a, b)) return false;
  if (a.player === null && b.player === null) return false;
  if (!ignoreLock && (a.locked || b.locked)) return false;
  if (a.slotKind === b.slotKind && a.slotKind !== 'starter') return false;
  return fits(a, b) && fits(b, a);
}

/** Why `b` is not a place for the player picked in `a`, in a sentence. */
function whyNot(a: LineupRowView, b: LineupRowView): string {
  const verb = (r: LineupRowView) =>
    r.slotKind === 'reserve' || r.slotKind === 'taxi' ? 'go on' : 'play';
  if (a.player !== null && !fits(a, b)) return `${a.player.shortName} can't ${verb(b)} ${b.slot}.`;
  if (b.player !== null && !fits(b, a)) return `${b.player.shortName} can't ${verb(a)} ${a.slot}.`;
  return 'Pick one of the highlighted rows.';
}

function LineupBody({
  view,
  teamId,
  week,
  onSaved,
  room,
}: {
  view: LineupView;
  teamId: string;
  /** Another week than now (D4); undefined is now. */
  week: number | undefined;
  onSaved: (view: LineupView) => void;
  /** A tool taking the room beside the dock (B-400). */
  room: SideTool | undefined;
}) {
  // ITS NAME FOR THE WAY BACK (B-398): the screen after a team's page says
  // "Back to" the team, which the address alone cannot name. A tool taking
  // the room is named by its address (Players, Waivers…).
  useScreenName(room !== undefined ? null : view.team.isMine ? 'My Team' : view.team.name);
  // Another team's matchup that week, from the week's scoreboard (B-220):
  // both, the first first, in a week of two games (Q6).
  const board = valueOf(useScoreboard(week));
  const theirs = view.team.isMine
    ? []
    : (board?.matchups ?? [])
        .filter((m) => m.home.team.id === view.teamId || m.away.team.id === view.teamId)
        .sort((a, b) => Number(a.secondGame) - Number(b.secondGame));
  const { swap } = useSim();
  const source = useLeagueSource();
  // The table's order, for another team's page's neighbors (B-378).
  const standings = useStandings();
  const myTeamId = useMyTeamId();
  // ONE SAVE AT A TIME. A swap's two cells are built from the rows on screen,
  // and a second tap before the first reply lands builds them from rows that
  // are already stale — the server checks one request, not two, and stored a
  // player in two slots (review of 2026-09-24). Taps wait for the answer.
  const [saving, setSaving] = useState(false);
  // A team that is not mine is read, never changed: no swaps, no slot picked
  // from a health bar, and no panels of mine beside it.
  // MINE is the page's: my doors, my news, what waits on me. EDITABLE is the
  // lineup's: a team knocked out (Q1, B-364) has no roster and nothing to
  // set, so no Fill on its empty slots and no "Your lineup is set." over them.
  const mine = view.team.isMine;
  const out = view.eliminatedPeriod !== null;
  // The league's own moment: the demo league's held Tuesday, the wall's elsewhere.
  const leagueNow = useLeagueNow();
  // A WEEK THAT IS OVER is history in a real league: its lineup is what
  // played, the server refuses a save to it, and putting it right is a
  // commissioner's fix (J1). The practice league keeps its own time.
  const weekOver = source === 'api' && Date.parse(view.period.end) <= leagueNow;
  const editable = mine && !out && !weekOver;
  // BEST BALL SETS THE LINEUP (Q2, B-363): no move between the bench and the
  // starters is mine to make. IR is the roster's, and still moves.
  // The taxi squad is the roster's too (§2.7).
  const held = (r: LineupRowView) => r.slotKind === 'reserve' || r.slotKind === 'taxi';
  const tradable = (a: LineupRowView, b: LineupRowView, ignoreLock = false): boolean =>
    canTrade(a, b, ignoreLock) && (!view.bestBall || held(a) || held(b));
  // Where a best-ball move can start: IR or the taxi squad, or a player who may go on either.
  const startsMove = (r: LineupRowView): boolean =>
    !view.bestBall || held(r) || r.reserveEligible || r.taxiEligible;
  // My team's name and colors, when the league on screen has a team of mine to
  // edit — the same one this lineup is (SCENARIOS L2).
  const editTeam = useEditTeam();
  const here = useMyTeamHere();
  const canEditTeam = mine && here !== null && here.team.id === view.teamId;
  // A COMMISSIONER'S FIX, for a week that is over and only in a real league
  // (COMMISSIONER_OVERRIDES.md, J1): any team's, their own included.
  const active = useActiveLeague();
  const canFix = active?.league.isCommissioner === true && weekOver;
  const [fixing, setFixing] = useState(false);
  const { play } = useDelight();
  // The row a move started from (B-275), until it ends or is canceled.
  const [picked, setPicked] = useState<Cell | null>(null);
  // THE BAR'S "DROP A PLAYER" (D9): the list itself is the picker, and the
  // drop is the one every screen makes (`RosterMoveSheet`).
  const [dropping, setDropping] = useState(false);
  const [move, setMove] = useState<RosterMove | null>(null);
  // The row a locked tap is shaking, for the moment it shakes.
  const [shaking, setShaking] = useState<string | null>(null);
  // The row a drag is over (B-288), as `slot-slotIndex`.
  const [over, setOver] = useState<string | null>(null);
  // HOW TO MOVE A PLAYER, SAID ONCE (B-288): a line over the lineup until this
  // device has made a move or been told it knows. A convenience of the
  // device's, so it lives in the browser and is shown when storage is not.
  const [tipSeen, setTipSeen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(MOVE_TIP_KEY) === '1';
    } catch {
      return false;
    }
  });
  const knowsHowToMove = () => {
    setTipSeen(true);
    try {
      window.localStorage.setItem(MOVE_TIP_KEY, '1');
    } catch {
      // A private window keeps no storage; the line comes back next visit.
    }
  };
  // A MOUSE CAN DRAG (B-288, DIRECTION U7): a desktop's rows drag onto each
  // other as well as moving by their badges. A finger keeps the badge: a drag
  // on a touch screen is a scroll.
  const fine = useFinePointer();
  const zone = useReaderZone();
  // The screen stays mounted from one team's page to the next (a name in the
  // sandbox, B-220), so a slot picked on mine is forgotten on theirs (the
  // review of 2026-09-27).
  const [card, setCard] = useState<PlayerCardData | null>(null);
  // THE SLOT A ROW CHOSE (B-320): who can fill it, beside the lineup or in a
  // sheet on a phone. Never kept: My Team opens on the free agents.
  const [chosen, setChosen] = useState<Cell | null>(null);
  // The card in the side panel goes with them: mine left open on their page
  // offered Move in lineup on a lineup that cannot change (the review of
  // 2026-10-13). So does a chosen slot, which is mine.
  useEffect(() => {
    setPicked(null);
    setDropping(false);
    setCard(null);
    setChosen(null);
  }, [teamId]);
  const [params, setParams] = useSearchParams();
  // Band 4, context: the game line under a name is what an average manager
  // glances at, so it starts at Standard. State (a bye, an Out) is band 2 and
  // is never hidden — see the row below.
  const showContext = useAtLeast('standard');
  // The helpers the league keeps (RULES_SCHEMA §15c): what is off is not
  // served, and its place is left out rather than drawn as a dash.
  const tools = useTools();
  const tier = useTier();
  // What needs me: read from the home view, which the league's other screens
  // already asked for, so it is not a second request.
  const home = valueOf(useHome());
  // What needs you is about now, so the strip shows on now's week only; the
  // side panel's counts are about now whichever week the lineup is on.
  const waiting = mine ? attentionItems(home?.attention ?? null) : [];
  const needs = week === undefined ? waiting : [];
  // BESIDE THE LINEUP FROM A TABLET UP (B-316): the panel on show, kept by the
  // league (UX-39), and a player's card opens in it rather than in a sheet
  // over the roster it is read against. A phone keeps the sheet.
  const wide = tier !== 'phone';
  const leagueKey = leagueIdFromPath(useLocation().pathname) ?? 'none';
  const [tool, setTool] = useRemembered<SideTool>(`side:${leagueKey}:tool`, 'players');
  // THE LINEUP AS STATS (B-413): the spreadsheet's other half, in a real
  // league, kept by the league as Players' switch is; another team's page
  // too since 2026-10-19 (B-461), from the pool's read of that team's roster.
  // THE WEEK AS IT IS NOW only (the review of B-461): the read is of the
  // roster held today and its ranges are the league's now, so a past week's
  // lineup would lose the players it no longer holds.
  const statsOffered = source === 'api' && week === undefined;
  const [sheetShows, setSheetShows] = useRemembered<'fantasy' | 'stats'>(
    `lineup:${leagueKey}:shows`,
    'fantasy',
  );
  const [statRangeKept, setStatRange] = useRemembered<StatRange>(
    `lineup:${leagueKey}:range`,
    'season',
  );
  // No projected range in a league that keeps no projections (§15c); one kept
  // from before it was turned off is the season's.
  const statRange: StatRange =
    statRangeKept === 'projected' && !tools.projections ? 'season' : statRangeKept;
  const showStats = statsOffered && sheetShows === 'stats';
  const cardFor = useCardFor();
  const navigate = useNavigate();
  const base = useLeagueBase();
  // A TOOL TAKING THE ROOM (B-400): the roster narrows to its dock and the
  // tool draws its whole screen beside it. My own team, from a tablet up; the
  // address is the tool's, so a link, a reload and Back all land on it.
  const roomy = room !== undefined && wide && view.team.isMine;
  const toolPath = (t: SideTool) => `${base}${TOOLS.find((x) => x.id === t)!.path}`;
  const onRoom = (grow: boolean) => {
    const shown = roomy ? room : tool;
    setTool(shown);
    // NOTHING HALF-DONE CROSSES OVER: a slot being filled or a move in
    // progress belongs to the shape it was started in (the review of
    // 2026-10-17), and the dock shows neither.
    setChosen(null);
    setPicked(null);
    setDropping(false);
    if (grow) morph(() => navigate(toolPath(shown)));
    // Giving the room back is going back, when My Team is where it came from,
    // so the lineup is where it was left and Back does not return to the room.
    else morph(() => (cameFrom(base) ? navigate(-1) : navigate(base)));
  };
  const claimsWaiting = waiting.reduce(
    (n, a) => (a.kind === 'waiver-pending' ? n + a.count : n),
    0,
  );
  const offersWaiting = waiting.filter((a) => a.kind === 'trade-offer').length;

  // Arriving from a health bar elsewhere: ?slot=QB&i=0 opens that slot for
  // choosing. The link takes you to the decision; it never makes it.
  const paramSlot = params.get('slot');
  const paramIndex = params.get('i');
  useEffect(() => {
    if (!editable) return;
    if (paramSlot === null || paramIndex === null) return;
    setPicked({ slot: paramSlot, slotIndex: Number(paramIndex) });
    const next = new URLSearchParams(params);
    next.delete('slot');
    next.delete('i');
    setParams(next, { replace: true });
    // Safe to re-run on `params`: this clears the two keys, so the next pass
    // returns at the guard above rather than looping.
  }, [editable, paramSlot, paramIndex, params, setParams]);

  // THE MOVE IN PROGRESS (B-275). Any row's badge picks it (a starter, a bench
  // player, an empty slot, an IR spot), and the rows it can trade places with
  // light up: one rule for every kind of row, so a move starts from either
  // end, as it does on ESPN and Sleeper.
  const allRows = [...view.starters, ...view.bench, ...view.reserve];
  const pickedRow = picked ? (allRows.find((r) => sameCell(r, picked)) ?? null) : null;
  // WHO IS IN THE CHOSEN SLOT NOW, from the rows on screen: kept at the
  // press, a move, a drag or an add left the list marking whoever was there
  // before (the review of 2026-10-14).
  const chosenNow: ChosenSlot | null =
    chosen === null
      ? null
      : {
          ...chosen,
          playerId: allRows.find((r) => sameCell(r, chosen))?.player?.id ?? null,
        };
  const targets = pickedRow === null ? [] : allRows.filter((r) => tradable(pickedRow, r));
  // AN IR ROW PICKED (D9, LINEUP.md "IR, and a roster that breaks its
  // rules"): its line offers the move off IR beside a trade of places.
  const selectedReserve =
    pickedRow !== null && pickedRow.slotKind === 'reserve' && view.reserveRule !== null
      ? pickedRow
      : null;
  const openBench = view.bench.find((r) => r.player === null && !r.locked) ?? null;
  /** Where a player on IR goes when he comes off: an open bench spot, else a starting slot he can play. */
  const offReserveTo = (r: LineupRowView): LineupRowView | null =>
    openBench ??
    view.starters.find((s) => s.player === null && !s.locked && r.eligibleSlots.includes(s.slot)) ??
    null;
  const openReserve =
    view.reserve.find((r) => r.slotKind === 'reserve' && r.player === null && !r.locked) ?? null;
  // IR AND THE TAXI SQUAD, each its own band (§2.7).
  const onReserve = view.reserve.filter((r) => r.slotKind === 'reserve');
  const onTaxi = view.reserve.filter((r) => r.slotKind === 'taxi');

  const doSwap = (slot: string, slotIndex: number, playerId: string | null) => {
    setPicked(null);
    if (source === 'api') {
      if (saving) return;
      // A SWAP IS TWO CELLS: the player going in takes the slot, and whoever
      // was there takes the cell he came from.
      const rows = [...view.starters, ...view.bench, ...view.reserve];
      const target = rows.find((r) => r.slot === slot && r.slotIndex === slotIndex);
      const from = playerId === null ? undefined : rows.find((r) => r.player?.id === playerId);
      const slots = [{ slot, slotIndex, playerId }];
      if (from) {
        slots.push({
          slot: from.slot,
          slotIndex: from.slotIndex,
          playerId: target?.player?.id ?? null,
        });
      }
      setSaving(true);
      void (async () => {
        const reply = await saveLineup(view.leagueId, teamId, { slots, period: view.period.index });
        setSaving(false);
        if (reply.status === 'ok') {
          onSaved(reply.data);
          play('lock');
          tell('Saved.', 'good');
          knowsHowToMove();
          return;
        }
        tell(
          messageOf(reply, 'That did not save.', 'Could not reach the site. Nothing was saved.'),
          reply.status === 'refused' ? 'warn' : 'bad',
        );
      })();
      return;
    }
    const err = swap(teamId, slot, slotIndex, playerId);
    if (err) tell(err, 'bad');
    else {
      play('lock');
      tell('Saved.', 'good');
      knowsHowToMove();
    }
  };

  /**
   * A player of mine into the chosen slot, from who-can-fill-it (B-379): the
   * move a badge makes, when the two can trade places; null when they cannot.
   */
  const placeInChosen = (playerId: string): (() => void) | null => {
    if (chosenNow === null || saving) return null;
    const target = allRows.find((r) => sameCell(r, chosenNow));
    const from = allRows.find((r) => r.player?.id === playerId);
    if (!target || !from || !tradable(from, target)) return null;
    return () => doSwap(target.slot, target.slotIndex, playerId);
  };

  /** The end of a move: the picked row and `r` trade places. */
  const finish = (r: LineupRowView) => {
    if (pickedRow === null) return;
    if (pickedRow.player !== null) doSwap(r.slot, r.slotIndex, pickedRow.player.id);
    else if (r.player !== null) doSwap(pickedRow.slot, pickedRow.slotIndex, r.player.id);
  };

  const cancel = () => {
    setPicked(null);
    setDropping(false);
  };

  /**
   * A BADGE'S PRESS (B-275): it picks its row, cancels the move it started,
   * or ends a move there. A badge that is not a place the picked player can
   * go becomes the new pick, the way a second badge works on the big sites.
   */
  const onSlot = (r: LineupRowView) => {
    if (saving || !editable) return;
    if (dropping) {
      if (r.player === null) return;
      setDropping(false);
      setMove({ kind: 'drop', playerId: r.player.id });
      return;
    }
    if (pickedRow !== null && sameCell(pickedRow, r)) {
      setPicked(null);
      return;
    }
    if (pickedRow !== null && tradable(pickedRow, r)) {
      finish(r);
      return;
    }
    // Any locked row says so and shakes, an empty one too: a league that
    // locks the whole lineup at once locks its empty slots with it.
    if (r.locked) {
      refuseLocked(r);
      return;
    }
    if (!startsMove(r)) {
      tell(
        'Best ball picks this lineup from the points after the games; only IR is yours to move.',
      );
      return;
    }
    setPicked({ slot: r.slot, slotIndex: r.slotIndex });
  };

  /**
   * A TAP ELSEWHERE ON A ROW: a bigger target for what its controls do. With
   * a move open it ends the move there, or says why it cannot; otherwise it
   * opens the player's card, and on an empty slot it starts filling it.
   */
  const onRow = (r: LineupRowView) => {
    if (saving) return;
    if (!editable) {
      openCard(r);
      return;
    }
    if (dropping) {
      onSlot(r);
      return;
    }
    if (pickedRow !== null) {
      if (sameCell(pickedRow, r)) setPicked(null);
      else if (tradable(pickedRow, r)) finish(r);
      else if (r.locked) refuseLocked(r);
      else tell(whyNot(pickedRow, r), 'bad');
      return;
    }
    choose(r);
  };

  // THE ROW IS THE SLOT (B-320, DIRECTION U21): a press anywhere but the name
  // and the badge lists who can fill it — whoever is in it, empty or not —
  // and the name is still the player's card. Pressed again, it is put back.
  const isChosen = (r: LineupRowView) =>
    chosen !== null && chosen.slot === r.slot && chosen.slotIndex === r.slotIndex;
  const choose = (r: LineupRowView) => {
    setCard(null);
    if (isChosen(r)) {
      setChosen(null);
      return;
    }
    setChosen({ slot: r.slot, slotIndex: r.slotIndex });
    if (wide) setTool('players');
  };
  /** The row's own button for it, for a keyboard and a screen reader; asleep during a move. */
  const fill = {
    label: (r: LineupRowView) => `Who can fill ${r.slot}`,
    chosen: isChosen,
    disabled: pickedRow !== null || dropping || saving,
    onPress: choose,
  };

  // A NAME DURING A MOVE IS THE MOVE (B-204). With a player picked, a name is
  // where a person taps to choose the other one, and opening his card instead
  // is the opposite of what they came for. Otherwise it is his card.
  const onName = (r: LineupRowView) => {
    if (editable && (pickedRow !== null || dropping)) onRow(r);
    else openCard(r);
  };

  /** What a badge's press does, for its name (it starts with the slot's own words). */
  const slotAction = (r: LineupRowView): string => {
    if (pickedRow !== null && sameCell(pickedRow, r)) return 'cancel this move';
    if (pickedRow !== null && tradable(pickedRow, r)) {
      // "Here" first: the word on the button is its name's first word.
      const mover = pickedRow.player ?? r.player;
      return mover !== null ? `here: move ${mover.shortName} here` : 'here';
    }
    if (r.locked) return r.player !== null ? `${r.player.name}, locked` : 'locked';
    if (r.player !== null) return `move ${r.player.name}`;
    if (r.slotKind === 'starter') return 'fill this slot';
    return 'move a player here';
  };
  // THE PRESS IN A WORD where there is room (B-288): Move, then Here on the
  // places it can go, Cancel on the one picked, Fill on an empty starter. A
  // phone has the arrow under the badge instead.
  const words = tier !== 'phone';
  const slotWord = (r: LineupRowView): string | null => {
    if (!words) return null;
    // No word on a locked row, but its badge keeps the column's line.
    if (r.locked) return '';
    if (pickedRow !== null && sameCell(pickedRow, r)) return 'Cancel';
    if (pickedRow !== null && tradable(pickedRow, r)) return 'Here';
    // While a move is open only Cancel and Here speak, so the places it can
    // go are the words on screen.
    if (pickedRow !== null) return '';
    if (!startsMove(r)) return '';
    if (r.player === null && r.slotKind === 'starter') return 'Fill';
    return 'Move';
  };

  /**
   * A DESKTOP'S DRAG (B-288): a row picked up is a move started from it, the
   * rows it can go to light as they do for the badge, and a drop on one ends
   * the move there. A drop anywhere else lets it go.
   */
  const dragProps = (r: LineupRowView): DragProps => {
    if (!editable || !fine || saving) return {};
    const key = `${r.slot}-${r.slotIndex}`;
    return {
      draggable: r.player !== null && !r.locked,
      onDragStart: (e) => {
        if (r.player === null || r.locked) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', r.player.name);
        setPicked({ slot: r.slot, slotIndex: r.slotIndex });
      },
      onDragOver: (e) => {
        if (pickedRow !== null && tradable(pickedRow, r)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (over !== key) setOver(key);
        } else if (over !== null) setOver(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        setOver(null);
        if (pickedRow !== null && tradable(pickedRow, r)) finish(r);
      },
      onDragEnd: (e) => {
        setOver(null);
        if (e.dataTransfer.dropEffect === 'none') setPicked(null);
      },
    };
  };

  const badgeState = (r: LineupRowView): 'picked' | 'target' | 'locked' | null => {
    if (pickedRow !== null && sameCell(pickedRow, r)) return 'picked';
    if (pickedRow !== null && tradable(pickedRow, r)) return 'target';
    if (r.locked) return 'locked';
    return null;
  };

  // Esc ends a move as Cancel does (UX_STANDARDS UX-5); with no move open it
  // puts away the card in the side panel, as a sheet's Esc closes it. ONE
  // HANDLER FOR BOTH, and it yields to anything open over the page — a sheet,
  // a dialog, a popover — whose own Esc this is: two window handlers closed
  // the card under the Edit team sheet and with every move (the review of
  // 2026-10-13).
  const sideCard = wide && card !== null;
  // And then the slot chosen beside the lineup (B-320); a phone's sheet
  // closes on its own Esc.
  const sideSlot = wide && chosen !== null;
  useEffect(() => {
    if (picked === null && !dropping && !sideCard && !sideSlot) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (picked !== null || dropping) {
        setPicked(null);
        setDropping(false);
        return;
      }
      if (document.querySelector('[role="dialog"], [role="tooltip"]') !== null) return;
      // Esc in a field is the field's: the panel's Order closed its list and
      // took the slot with it (the review of 2026-10-14).
      if (e.target instanceof Element && e.target.closest('input, textarea, select') !== null)
        return;
      if (sideCard) setCard(null);
      else setChosen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picked, dropping, sideCard, sideSlot]);

  // A LOCKED ROW SAYS SO AND SHAKES (LINEUP.md, B-221): "Locked at Sun 11:00
  // AM MDT kickoff." It said "Locked at Final." — the game's label, which
  // after kickoff is its score, not the time it locked.
  const refuseLocked = (r: LineupRowView) => {
    tell(lockWords(r, zone));
    const key = `${r.slot}-${r.slotIndex}`;
    setShaking(key);
    window.setTimeout(() => setShaking((k) => (k === key ? null : k)), 450);
  };

  /** The health bar's one move for a problem that has one (D9). */
  const onFix = (p: LineupProblem) => {
    if (saving) return;
    const at = [...view.starters, ...view.reserve].find(
      (r) => r.slot === p.rowSlot && r.slotIndex === p.rowSlotIndex,
    );
    if (p.fix === 'to-reserve' && openReserve && at?.player) {
      doSwap(openReserve.slot, openReserve.slotIndex, at.player.id);
    } else if (p.fix === 'off-reserve' && at?.player) {
      const to = offReserveTo(at);
      if (to) doSwap(to.slot, to.slotIndex, at.player.id);
    } else if (p.fix === 'drop') {
      setPicked(null);
      setDropping(true);
    }
  };

  const openCard = (r: LineupRowView) => {
    if (!r.player) return;
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      points: r.points,
      statLine: r.statLine,
      action: null,
      // THE CARD MOVES HIM TOO (B-288): a name is where most people tap
      // first, so the card they land on starts the same move the badge does.
      move:
        editable && !r.locked
          ? {
              label: 'Move in lineup',
              onClick: () => {
                setCard(null);
                setPicked({ slot: r.slot, slotIndex: r.slotIndex });
              },
            }
          : null,
    });
  };

  const renderRow = (r: LineupRowView, kind: 'starter' | 'bench') => {
    // His game has kicked off (or is over): the row's number is his points.
    const started = r.player !== null && (r.game?.state === 'live' || r.game?.state === 'final');
    const state = rowState(r);
    const cls = [
      'fsp-row',
      'fsp-row-clickable',
      state === 'selected' ? 'fsp-row-selected' : '',
      (kind === 'starter' &&
        (r.health === 'out' || r.health === 'empty' || r.health === 'bye') &&
        !r.locked) ||
      r.health === 'illegal'
        ? 'fsp-row-problem'
        : '',
      state === 'eligible' ? 'fsp-row-eligible' : '',
      state === 'dim' ? 'fsp-row-dim' : '',
      shaking === `${r.slot}-${r.slotIndex}` ? 'fsp-shake' : '',
    ].join(' ');
    return (
      <div
        key={`${r.slot}-${r.slotIndex}`}
        className={`${cls} fsp-lineup-row`}
        // A locked row is drawn quieter (B-221): it cannot move, and the rows
        // that still can are the ones a manager is looking for.
        data-locked={(r.player !== null && r.locked) || undefined}
        // THE ROW IS A BIGGER TARGET, NOT A CONTROL OF ITS OWN (B-275): the
        // badge and the name are its buttons, for a keyboard and a screen
        // reader, and a tap anywhere else on it does what they do.
        data-ux-convenience=""
        data-words={(editable && words) || undefined}
        data-over={over === `${r.slot}-${r.slotIndex}` || undefined}
        data-fill={editable || undefined}
        data-chosen={(editable && isChosen(r)) || undefined}
        // `s` and `b` press the badge (M5, `shell/keyboardMap.tsx`).
        data-kbd-move={editable ? (r.slotKind === 'starter' ? 'b' : 's') : undefined}
        onClick={() => onRow(r)}
        {...dragProps(r)}
      >
        {editable ? (
          <SlotButton
            pos={r.slot}
            action={slotAction(r)}
            state={badgeState(r)}
            word={slotWord(r)}
            onClick={() => onSlot(r)}
          />
        ) : (
          <PositionPill pos={r.slot} />
        )}
        {r.player ? (
          <div className="fsp-lineup-who">
            <div className="fsp-name">
              <button
                type="button"
                className="fsp-linklike fsp-name-text fsp-name-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onName(r);
                }}
              >
                {r.player.name}
              </button>
              <ProTeamCode code={r.player.proTeam} />
              {/* A BYE ONCE, WHERE HIS GAME IS (B-521): the row read "BYE",
                  "Bye" and "On bye". His injury still shows. */}
              <StatusBadge player={{ ...r.player, onBye: false }} />
              {/* WHO COUNTS IN BEST BALL (Q2, B-363): the slot his points
                  count in, by the points so far, wherever he is stored. */}
              {r.countsAs !== null ? (
                <Chip tone="accent">
                  {r.countsAs === r.slot ? 'Counts' : `Counts at ${r.countsAs}`}
                </Chip>
              ) : null}
              {r.locked ? (
                // THE PADLOCK AND WHEN (B-221). A phone keeps the name's room
                // and gives the time on a tap; wider, it sits beside the lock.
                // Its words when asked (B-374): a native title never reached
                // a finger.
                <Term className="fsp-lock" tip={lockWords(r, zone)} name="">
                  <Icon name="locked" size="sm" />
                  {tier !== 'phone' && lockTime(r, zone) !== null ? (
                    <span className="fsp-lock-time">{lockTime(r, zone)}</span>
                  ) : null}
                </Term>
              ) : null}
            </div>
            <div className="fsp-sub">
              {showContext && r.statLine ? (
                // THE GAME'S STATE STAYS BESIDE THE STAT LINE (B-213): Simple
                // shows "Final", so Standard must not lose it for the numbers.
                <>
                  {r.game?.state === 'final' ? 'Final · ' : null}
                  {r.game?.state === 'live' && r.game.score !== null ? <LiveDot /> : null}
                  {r.statLine}
                </>
              ) : (
                <>
                  <span className="fsp-game-line">
                    <span className="fsp-game-line-text">
                      <GameLabel game={r.game} />
                    </span>
                    {r.game?.state === 'pre' ? (
                      <MatchupChip
                        cue={r.matchup}
                        position={r.player.positions[0]}
                        opponent={r.game.opponent}
                      />
                    ) : null}
                  </span>
                </>
              )}
            </div>
            {projectionNote(r.projection, r.basis) !== null && r.game?.state !== 'bye' ? (
              <div className="fsp-sub fsp-muted fsp-ui-wrap-text">
                {projectionNote(r.projection, r.basis)}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="fsp-muted">
            {editable ? 'Empty. Tap to see who can fill it.' : 'Empty.'}
          </div>
        )}
        <div className="fsp-right">
          <div className={`fsp-num fsp-lineup-points ${r.points !== null ? 'scored' : ''}`}>
            {r.points !== null ? (
              <PointsWhy
                player={r.player}
                points={r.points}
                breakdown={r.breakdown}
                statLine={r.statLine}
              >
                {r.points.toFixed(1)}
              </PointsWhy>
            ) : started ? (
              // HIS GAME HAS KICKED OFF AND HIS LINE HAS NOT LANDED: a dash,
              // never his projection in the points' place and never a 0 — the
              // screen's note says when the numbers were last updated (N5).
              <span className="fsp-muted">—</span>
            ) : r.player !== null && !tools.projections ? null : r.player !== null ? (
              // Before kickoff the number is his projection, and it says so
              // (B-295): with nothing over the column, a bare 14.2 read as a
              // score.
              <Proj>
                <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
                  {num(r.projection)}
                </ProjectionWhy>
              </Proj>
            ) : (
              num(r.projection)
            )}
          </div>
          {(r.points !== null || started) && showContext && tools.projections ? (
            <div className="fsp-sub fsp-num">
              proj{' '}
              <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
                {num(r.projection)}
              </ProjectionWhy>
            </div>
          ) : null}
        </div>
        {/* The slot's own button (B-320): what a press elsewhere on the row
            does, for a keyboard and a screen reader. */}
        {editable ? (
          <IconButton
            icon="next"
            variant="quiet"
            size="sm"
            label={fill.label(r)}
            aria-pressed={fill.chosen(r)}
            disabled={fill.disabled}
            onClick={(e) => {
              e.stopPropagation();
              fill.onPress(r);
            }}
          />
        ) : null}
      </div>
    );
  };

  // THE MOVE IN PROGRESS, IN WORDS, WITH A WAY OUT (UX_STANDARDS UX-5): what is
  // picked and what to tap next, and a Cancel. Pinned to the top of the list,
  // so a move started at the bench still says what it is doing.
  const cancelButton = (
    <Button variant="quiet" size="xs" onClick={cancel}>
      Cancel
    </Button>
  );
  const moveWords = (): ReactNode => {
    if (pickedRow === null) return null;
    const p = pickedRow.player;
    if (targets.length === 0) {
      // WHY THERE IS NOWHERE TO GO, truly (the review of 2026-10-11): a
      // backup whose game has started is a lock, not an empty bench.
      const lockedOnly = allRows.some((r) => r.locked && tradable(pickedRow, r, true));
      if (lockedOnly)
        return p === null
          ? `Everyone who could play ${pickedRow.slot} is locked: their games have started.`
          : `Every place ${p.shortName} could go is locked: those games have started.`;
      if (p === null || pickedRow.slotKind === 'starter')
        return `Nobody on the bench can play ${pickedRow.slot}.`;
      return `There is no open slot ${p.shortName} can play.`;
    }
    // With the words on screen (B-288), the line names the word to press.
    const aPlayer = words ? 'press Here beside a player' : 'tap a highlighted player';
    const aSlot = words ? 'press Here beside a slot' : 'tap a highlighted slot';
    if (p === null)
      return pickedRow.slotKind === 'starter'
        ? `${pickedRow.slot} is empty: ${aPlayer} to fill it.`
        : `${words ? 'Press Here beside a player' : 'Tap a highlighted player'} to move him here.`;
    if (pickedRow.slotKind === 'starter')
      return `Moving ${p.shortName}: ${aPlayer} to trade places.`;
    return `Moving ${p.shortName}: ${aSlot} to put him there.`;
  };
  const moveTip =
    view.eliminatedPeriod !== null ? (
      // KNOCKED OUT (Q1, B-364): no roster, and nothing here to set.
      <div className="fsp-lineup-hint" role="note">
        <span>
          {view.team.name} went out after Week {view.eliminatedPeriod}, and its players left for the
          rest of the league.
        </span>
      </div>
    ) : view.bestBall ? (
      // Said plainly and always: nothing here is a lineup to set (LINEUP.md).
      <div className="fsp-lineup-hint" role="note">
        <span>
          Best ball: the best lineup counts, picked from the points after the games, and nobody sets
          one. The players marked Counts are in it so far.
        </span>
      </div>
    ) : editable && !tipSeen ? (
      <div className="fsp-lineup-hint fsp-lineup-tip" role="note">
        <Icon name="move" size="sm" />
        <span>
          {!words
            ? 'Tap a position badge (QB, RB, BN…) to move a player, then tap where he goes.'
            : fine
              ? 'Click Move beside a player, then Here where he goes. Or drag him there.'
              : 'Tap Move beside a player, then Here where he goes.'}
        </span>
        <Button variant="quiet" size="xs" onClick={knowsHowToMove}>
          Got it
        </Button>
      </div>
    ) : null;
  const moveHint = dropping ? (
    <div className="fsp-lineup-hint" role="status">
      <span>Pick a player to drop.</span>
      {cancelButton}
    </div>
  ) : selectedReserve ? (
    <div className="fsp-lineup-hint" role="status">
      <span>
        {selectedReserve.player === null ? (
          <>Pick a player to move to {selectedReserve.slot}.</>
        ) : (
          <>
            Pick a player to trade places with {selectedReserve.player.shortName}
            {offReserveTo(selectedReserve) !== null ? (
              <>
                , or{' '}
                <button
                  type="button"
                  className="fsp-linklike"
                  onClick={() => {
                    const to = offReserveTo(selectedReserve)!;
                    doSwap(to.slot, to.slotIndex, selectedReserve.player!.id);
                  }}
                >
                  move {selectedReserve.player.shortName} off {selectedReserve.slot}
                </button>
              </>
            ) : null}
            .
          </>
        )}
      </span>
      {cancelButton}
    </div>
  ) : pickedRow !== null ? (
    <div className="fsp-lineup-hint" role="status">
      <span>{moveWords()}</span>
      {cancelButton}
    </div>
  ) : null;

  /** How a row reads while a move is open: the one picked, one it can go to, or one it cannot. */
  const rowState = (r: LineupRowView): 'selected' | 'eligible' | 'dim' | null => {
    if (pickedRow === null) return null;
    if (sameCell(pickedRow, r)) return 'selected';
    return tradable(pickedRow, r) ? 'eligible' : 'dim';
  };
  // A TINTED BAND, NOT A ROW (B-215): a section label drawn like a row blends
  // into the player rows, and it has to stand apart from them.
  const head = (label: string) => (
    <div className="fsp-lineup-section" role="heading" aria-level={3}>
      <span className="fsp-cap">{label}</span>
    </div>
  );

  // THE STARTERS' TOTAL, on the lineup's own head (B-315): the sandbox's half
  // that is about this team (D7). It moves as players swap, and once games
  // start it is the points so far beside the projection. The opponent's total
  // and the chance to win are Game Day's (DIRECTION U22).
  // Its name is its words (axe's label-content-name-mismatch): what a screen
  // reader says starts with what the eye reads.
  // A LEAGUE THAT TURNED THE TOTAL OFF (or projections, which it is built
  // from) keeps the points so far, and says so where the total would be when
  // projections are off, at Standard and up (RULES_SCHEMA §15c).
  const projected = tools.sandbox ? view.projectedTotal : null;
  const totalWords = [
    view.liveTotal !== null ? `${view.liveTotal.toFixed(1)} pts` : null,
    projected !== null ? `${num(projected)} proj` : null,
  ]
    .filter((w) => w !== null)
    .join(' · ');
  const total =
    projected === null && view.liveTotal === null ? (
      !tools.projections && showContext ? (
        <Text tone="secondary" size="xs">
          Projections are off in this league
        </Text>
      ) : null
    ) : (
      <Term
        name={totalWords}
        tip={
          projected === null
            ? "The starters' points so far."
            : "The starters' total: points so far plus what is projected for the games still to come. It moves as you swap players."
        }
      >
        <span className="fsp-lineup-total">
          {view.liveTotal !== null ? (
            <>
              <span className="fsp-num">{view.liveTotal.toFixed(1)}</span>
              <span className="fsp-sub">{projected !== null ? ' pts · ' : ' pts'}</span>
            </>
          ) : null}
          {projected !== null ? (
            <>
              <span className="fsp-num">{num(projected)}</span>
              <span className="fsp-sub"> proj</span>
            </>
          ) : null}
        </span>
      </Term>
    );

  const lineupSheet = (
    <div>
      {statsOffered ? (
        <div className="fsp-lineup-shows">
          <span className="fsp-lineup-shows-switch">
            <OneOf
              label="Show"
              hideLabel
              options={[
                { value: 'fantasy', label: 'Fantasy' },
                { value: 'stats', label: 'Stats' },
              ]}
              value={sheetShows}
              onChange={setSheetShows}
            />
          </span>
          {showStats ? (
            <span className="fsp-lineup-shows-range">
              <Choice
                label="Which games"
                hideLabel
                searchable={false}
                required
                value={statRange}
                onChange={(v) => {
                  if (v) setStatRange(v as StatRange);
                }}
                options={STAT_RANGES.filter(
                  (r) => tools.projections || r.value !== 'projected',
                ).map((r) => ({ value: r.value, label: r.label }))}
              />
            </span>
          ) : null}
        </div>
      ) : null}
      {showStats ? (
        <>
          {/* A TEAM KNOCKED OUT says so here too, its players gone (the review of
            B-461); the Fantasy view's move tips are for a table this is not. */}
          {view.eliminatedPeriod !== null ? moveTip : null}
          <MyStats
            rows={[...view.starters, ...view.bench, ...view.reserve]}
            range={statRange}
            short={tier === 'phone'}
            onOpen={openCard}
            // BY ITS ID, MINE TOO (B-514): a team's read is a table per
            // position, where `mine` is the pool's, read by kind of play.
            teamId={view.teamId}
          />
        </>
      ) : (
        <>
          {moveHint ? null : moveTip}
          <LineupSheet
            // The move's line rides in the table's head, so the column
            // names never cover it or its Cancel (UX-36).
            banner={moveHint}
            shaking={shaking}
            groups={[
              { label: 'Starters', rows: view.starters },
              { label: 'Bench', rows: view.bench },
              ...(onReserve.length
                ? [{ label: 'Reserve', note: view.reserveRule, rows: onReserve }]
                : []),
              ...(onTaxi.length ? [{ label: 'Taxi', note: view.taxiRule, rows: onTaxi }] : []),
            ]}
            onRow={onRow}
            onName={onName}
            rowState={rowState}
            fill={editable ? fill : undefined}
            slot={
              editable
                ? {
                    onPress: onSlot,
                    action: slotAction,
                    state: badgeState,
                    word: slotWord,
                    drag: dragProps,
                    over,
                  }
                : undefined
            }
          />
        </>
      )}
    </div>
  );
  // ONE LIST, THE WAY EVERY FANTASY SITE DRAWS A ROSTER (B-215): the
  // starters, then the bench directly under them, then the reserve.
  const lineupList = (
    <div>
      {moveHint ?? moveTip}
      {head('Starters')}
      {view.starters.map((r) => renderRow(r, 'starter'))}
      {head('Bench')}
      {view.bench.map((r) => renderRow(r, 'bench'))}
      {onReserve.length ? (
        <>
          {head('Reserve')}
          {view.reserveRule !== null ? (
            // WHO MAY GO ON IR, SHOWN NOT DISCOVERED (D9).
            <div className="fsp-sub fsp-lineup-rule fsp-ui-wrap-text">{view.reserveRule}</div>
          ) : null}
          {onReserve.map((r) => renderRow(r, 'bench'))}
        </>
      ) : null}
      {onTaxi.length ? (
        <>
          {head('Taxi')}
          {view.taxiRule !== null ? (
            // WHO MAY GO ON THE TAXI SQUAD, AND UNTIL WHEN (§2.7).
            <div className="fsp-sub fsp-lineup-rule fsp-ui-wrap-text">{view.taxiRule}</div>
          ) : null}
          {onTaxi.map((r) => renderRow(r, 'bench'))}
        </>
      ) : null}
    </div>
  );

  // WHAT NEEDS THE TEAM, at the top (DIRECTION U13, B-315): a slot that
  // cannot score, what waits on me, and my players' news. From a tablet up
  // the news sits beside the other two rather than under them, so the lineup
  // keeps its height.
  const news =
    mine && week === undefined ? (
      <TeamNews
        shown={wide ? 2 : 3}
        onAll={() => {
          if (wide) {
            setCard(null);
            setTool('news');
          } else navigate(`${base}/news`);
        }}
      />
    ) : null;
  // ANOTHER TEAM'S PAGE (B-378): a trade with them, the team before and
  // after in the table, and on a phone their weeks ahead, which a wider
  // screen has in its side panel.
  const table = valueOf(standings)?.rows ?? [];
  const at = table.findIndex((r) => r.team.id === view.teamId);
  const neighbor = (step: number) => {
    if (at < 0 || table.length < 2) return null;
    const r = table[(at + step + table.length) % table.length]!;
    return { name: r.team.name, to: `${base}/teams/${r.team.id}` };
  };
  const visiting: Visiting | null = mine
    ? null
    : {
        trade:
          source === 'api' && myTeamId !== null && myTeamId !== view.teamId
            ? `${base}/trades?new=1&with=${view.teamId}`
            : null,
        ahead: !wide && source === 'api' ? `${base}/plan?team=${view.teamId}` : null,
        // Not in a league played against the field, whose schedule has my
        // week against the league alone.
        season:
          source === 'api' && !playsTheField(active?.league.format.id ?? '')
            ? `${base}/schedule?team=${view.teamId}`
            : null,
        prev: neighbor(-1),
        next: neighbor(1),
      };
  const status = (
    <div className="fsp-lineup-status">
      <TeamHeader
        view={view}
        onEdit={canEditTeam ? editTeam.open : undefined}
        onFix={canFix ? () => setFixing(true) : undefined}
        theirs={theirs}
        visiting={visiting}
      />
      <div className="fsp-team-alerts">
        <div className="fsp-team-alerts-main">
          {out ? null : (
            <HealthBar
              health={view.health}
              onOpen={editable ? (slot, slotIndex) => setPicked({ slot, slotIndex }) : undefined}
              onFix={editable && source === 'api' ? onFix : undefined}
              reserveSlot={view.reserve.find((r) => r.slotKind === 'reserve')?.slot ?? null}
              locked={view.starters.filter((r) => r.player !== null && r.locked).length}
            />
          )}
          <AttentionStrip items={needs} now={home?.updatedAt ?? view.updatedAt} />
        </div>
        {news}
      </div>
      {/* WHEN THE NUMBERS LANDED (N5): the starters' total and the rows' dashes
          read as scores without it while a week's lines are still to come. It
          was the sandbox's, and went with it until the review of 2026-10-13. */}
      <StatsNote stats={view.stats} />
      {/* The panels' screens, one tap away on a phone (B-316). */}
      {!wide && mine && week === undefined ? (
        <TeamDoors claims={claimsWaiting} offers={offersWaiting} />
      ) : null}
    </div>
  );

  // THE DOCK (B-400): slot and name, the marks for a slot that cannot score,
  // the lock; a name opens its card beside, a slot lists who can fill it.
  const dock = (
    <RosterDock
      starters={view.starters}
      bench={view.bench}
      reserve={view.reserve}
      onName={openCard}
      onSlot={
        editable
          ? (r) => {
              // Who can fill it is a list of players: Players takes the room.
              if (room !== 'players') navigate(toolPath('players'), { replace: true });
              choose(r);
            }
          : null
      }
      chosen={isChosen}
    />
  );

  const screen = (
    <WorkbenchScreen
      // TAKING THE ROOM, My Team's status gives its height to the tool: the
      // dock's marks still say what cannot score, and Collapse brings it back.
      status={roomy ? undefined : status}
      room={roomy}
      main={
        roomy
          ? { title: 'Lineup', content: dock }
          : { title: 'Lineup', meta: total, content: lineupList, sheet: lineupSheet }
      }
      side={
        // Another team's panel is its weeks ahead, which only a real league
        // has; in the practice league its lineup takes the width.
        wide && (mine || source === 'api') ? (
          <TeamSide
            teamId={view.teamId}
            mine={mine}
            tool={roomy ? room : tool}
            room={roomy}
            onRoom={mine && week === undefined ? onRoom : undefined}
            onTool={(t) => {
              setCard(null);
              setChosen(null);
              setTool(t);
              // In the room, another tool is another address, in place.
              if (roomy) navigate(toolPath(t), { replace: true });
            }}
            card={card}
            onCard={setCard}
            onMove={setMove}
            counts={{ claims: claimsWaiting, offers: offersWaiting }}
            slot={editable ? chosenNow : null}
            onSlot={setChosen}
            onPlace={editable ? placeInChosen : undefined}
          />
        ) : undefined
      }
    />
  );

  return (
    <>
      {/* A NAME ANYWHERE ON THE PAGE OPENS ITS CARD IN THE SIDE PANEL from a
          tablet up — the news, a claim's drop, a trade — as the lineup's own
          names do (B-316). */}
      {wide ? (
        <PlayerCardsOpenIn open={(ref) => setCard(cardFor(ref))}>{screen}</PlayerCardsOpenIn>
      ) : (
        screen
      )}
      {/* A phone's slot list is a sheet over the lineup (B-320), under the
          card a name in it opens. */}
      {wide || !editable ? null : (
        <DetailSheet
          opened={chosenNow !== null}
          // A card opened from the list takes Esc first; one press closed both.
          closeOnEscape={card === null}
          onClose={() => setChosen(null)}
          title="Who can fill this slot"
        >
          {chosenNow !== null ? (
            <SlotPanel
              chosen={chosenNow}
              onOpen={setCard}
              onMove={setMove}
              onPlace={placeInChosen}
            />
          ) : null}
        </DetailSheet>
      )}
      {wide ? null : <PlayerCard data={card} onClose={() => setCard(null)} />}
      {/* EVERY LEAGUE'S: the side panel's Add and Claim make their moves here,
          the practice league's included; the sheet answers the simulator
          itself, as it does on Players. It was a real league's only while the
          health bar's drop was the one move on this screen. */}
      <RosterMoveSheet
        move={move}
        showProjection={tools.projections}
        onClose={() => setMove(null)}
      />
      {canFix && active !== null ? (
        <FixLineupSheet
          view={view}
          leagueId={active.league.id}
          opened={fixing}
          onClose={() => setFixing(false)}
        />
      ) : null}
    </>
  );
}
